import {
    CylinderGeometry,
    Matrix4,
    MeshBasicMaterial,
    Quaternion,
    SphereGeometry,
    Vector3,
} from 'three';
import { markRaw, reactive } from 'vue';
import { watchImmediate } from '@vueuse/core';
import { type StopHandle, stopAll } from '@/util/stopHandle';
import type { PlannerSceneViewModel } from '@/views/3d-planner/store/plannerSceneViewModel.types';
import type { LtResectionResult } from '@/views/3d-planner/measurements/ltResectionDistance';
import type { PlannerSceneContext } from '@/views/3d-planner/scene/plannerSceneContext';
import { meshNode } from '@/lib/threeScene/nodes/mesh';
import { updateNode } from '@/lib/threeScene/updateNode';
import { formusLightTheme } from '@/plugins/formusTheme';

const LT_COLOR = formusLightTheme.colors!['point-pink'];
const LT_SPHERE_RADIUS = 1;
const SPHERE_SEGMENTS = 16;
const LINE_RADIUS = 0.1;
const LINE_SEGMENTS = 8;

// Shared geometry and material — reused across calls, never disposed
const sharedSphereGeometry = markRaw(
    new SphereGeometry(LT_SPHERE_RADIUS, SPHERE_SEGMENTS, SPHERE_SEGMENTS),
);
const sharedMaterial = markRaw(
    new MeshBasicMaterial({ color: LT_COLOR, depthTest: false }),
);
// Unit-height cylinder scaled via transform to match distance
const sharedCylinderGeometry = markRaw(
    new CylinderGeometry(LINE_RADIUS, LINE_RADIUS, 1, LINE_SEGMENTS),
);

/** The landmark sphere + connecting-line transforms for an LT-resection result. */
export type LtResectionTransforms = {
    landmarkTransform: Matrix4;
    /** Null when the landmark and nearest point coincide (degenerate line). */
    lineTransform: Matrix4 | null;
};

/**
 * Pure geometry: place the landmark sphere at the LT point and stretch the unit-height cylinder
 * to span from the LT point to the nearest resection-plane point.
 */
export function ltResectionTransforms(result: LtResectionResult): LtResectionTransforms {
    const landmarkTransform = new Matrix4().setPosition(
        result.ltPoint.x,
        result.ltPoint.y,
        result.ltPoint.z,
    );

    const direction = new Vector3().subVectors(result.nearestPoint, result.ltPoint);
    const length = direction.length();
    if (length < 1e-6) {
        return { landmarkTransform, lineTransform: null };
    }

    const midpoint = new Vector3()
        .addVectors(result.ltPoint, result.nearestPoint)
        .multiplyScalar(0.5);

    // CylinderGeometry is aligned along Y — rotate to match direction
    const quaternion = new Quaternion().setFromUnitVectors(
        new Vector3(0, 1, 0),
        direction.clone().normalize(),
    );

    const lineTransform = new Matrix4().compose(midpoint, quaternion, new Vector3(1, length, 1));
    return { landmarkTransform, lineTransform };
}

/**
 * Show the LT-resection landmark and distance line when the stem-neck-cross-section is visible
 * and LT resection data is available.
 *
 * The landmark and line are children of ltResectionGroup — toggling the group's
 * visibility controls both nodes at once. The resection result is supplied by the caller (the
 * feature ViewModel's `result`), so this stays a pure state-in / scene-out function that imports
 * no store — the View wires the ViewModel to it (per docs/threejs-practices.md).
 */
export function updateLtResectionVisualisation(
    context: PlannerSceneContext,
    scene: PlannerSceneViewModel,
    resectionResult: () => LtResectionResult | null,
): StopHandle {
    const group = scene.nodes.ltResectionGroup;
    const groupId = group.id;

    const landmarkNode = reactive(meshNode('lt-resection-landmark', {
        parent: groupId,
        geometry: sharedSphereGeometry,
        material: sharedMaterial,
    }));

    const lineNode = reactive(meshNode('lt-resection-line', {
        parent: groupId,
        geometry: sharedCylinderGeometry,
        material: sharedMaterial,
    }));

    return stopAll(
        updateNode(context, landmarkNode),
        updateNode(context, lineNode),
        watchImmediate(
            () => [scene.visibility['stem-neck-cross-section'], resectionResult()] as const,
            ([visibility, result]) => {
                const visible = visibility === 'on' && result !== null;
                group.visible = visible;

                if (!visible || !result) return;

                const { landmarkTransform, lineTransform } = ltResectionTransforms(result);
                // Mutate the node transforms in place (never reassign) so consumers holding the
                // Matrix4 reference see the update — see docs/vue-practices.md § Reactive object identity.
                landmarkNode.transform.copy(landmarkTransform);

                lineNode.visible = lineTransform !== null;
                if (lineTransform) {
                    lineNode.transform.copy(lineTransform);
                }
            },
        ),
        () => {
            group.visible = false;
        },
    );
}
